import { orderBy as orderByList } from 'lodash';
import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from '../utils/objectUtils';
import { create, getAll, getById, getByIdQuery, getBySingleQuery, remove, update, updateField, updateFields } from '../utils/dbUtils';
import moment from 'moment-timezone';
import { asTimeZone } from '../utils/dateFormatter';
import { db } from '../firebase';
import { collection, doc, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where, writeBatch } from 'firebase/firestore';

class Notification {
	static collectionName = 'notifications';

	userId = null;
	toStaff = false;

	subject = null;
	body = null;
	type = null; // INFO, WARNING, ERROR, SUCCESS, SILENT
	severity = null; // CRITICAL, HIGH, MEDIUM, LOW

	entityType = null; // Order/Encounter/Subscription/Prescription/MealPlan/Lead
	entityId = null;
	entityStatus = null;

	eventType = null;

	read = false;
	readAt = null;

	cta = { label: null, url: null };

	constructor(obj) {
		if (isObjectNotEmpty(obj)) {
			if (isStringNotEmpty(obj.userId)) { this.userId = obj.userId; }
			if (isBooleanTrue(obj.toStaff)) { this.toStaff = true; }


			if (isStringNotEmpty(obj.subject)) { this.subject = obj.subject; }
			if (isStringNotEmpty(obj.body)) { this.body = obj.body; }
			if (isStringNotEmpty(obj.type)) { this.type = obj.type; }
			if (isStringNotEmpty(obj.severity)) { this.severity = obj.severity; }

			if (isStringNotEmpty(obj.entityType)) { this.entityType = obj.entityType; }
			if (isStringNotEmpty(obj.entityId)) { this.entityId = obj.entityId; }
			if (isStringNotEmpty(obj.entityStatus)) { this.entityStatus = obj.entityStatus; }
			if (isStringNotEmpty(obj.eventType)) { this.eventType = obj.eventType; }

			if (isStringNotEmpty(obj.eventType)) { this.eventType = obj.eventType; }

			if (isBooleanTrue(obj.read)) { this.read = true; }
			if (isNotNullOrUndefined(obj.readAt)) { this.readAt = obj.readAt; }

			if (isObjectNotEmpty(obj.cta)) { this.cta = obj.cta; }
		}
	}

	toDto = () => {
		return {
			userId: this.userId,
			toStaff: this.toStaff,

			subject: this.subject,
			body: this.body,
			type: this.type,
			severity: this.severity,

			entityType: this.entityType,
			entityId: this.entityId,
			entityStatus: this.entityStatus,

			eventType: this.eventType,

			read: this.read,
			readAt: this.readAt,

			cta: this.cta,
		};
	}

	create = async () => {
		return await create(Notification.collectionName, this.toDto());
	}

	static updateField = async (id, fieldName, newValue) => {
		if (isStringNotEmpty(id)) {
			return await updateField(Notification.collectionName, id, fieldName, newValue);
		} else {
			throw new Error("id is required.");
		}
	}

	static updateFields = async (id, updatedFields) => {
		if (isStringNotEmpty(id)) {
			return await updateFields(Notification.collectionName, id, updatedFields);
		} else {
			throw new Error("id is required.");
		}
	}

	static update = async (id, updatedEntity) => {
		const updatedObj = new Notification(updatedEntity);
		if (id === updatedEntity.id) {
			return await update(Notification.collectionName, id, updatedObj.toDto());
		} else {
			throw new Error('id and id field of the updated notification must match.')
		}
	}

	static delete = async (id) => {
		if (isStringNotEmpty(id)) {
			return await remove(Notification.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static markRead = async (id) => {
		if (isStringNotEmpty(id)) {
			return await updateFields(Notification.collectionName, id, { read: true, readAt: moment().utc().toDate() });
		} else {
			throw new Error("id is required.");
		}
	}

	static markAllRead = async (messages) => {
		let updatedIds = [];
		if (isListNotEmpty(messages)) {
			let chunks = [];
			for (let i = 0; i < messages.length; i += 500) {
				chunks.push(messages.slice(i, i + 500))
			}
			for (const chunk of chunks) {
				// Get a new write batch
				const batch = writeBatch(db);
				chunk.forEach((item) => {
					if (isBooleanFalse(item.read)) {
						const messageRef = doc(collection(db, Notification.collectionName), item.id);
						batch.update(messageRef, { read: true, readAt: moment().utc().toDate() });
						updatedIds.push(item.id);
					}
				});
				await batch.commit();
			}
		}

		return updatedIds;
	}

	static markUnread = async (id) => {
		if (isStringNotEmpty(id)) {
			return await updateFields(Notification.collectionName, id, { read: false, readAt: null });
		} else {
			throw new Error("id is required.");
		}
	}

	static getById = async (id) => {
		if (isStringNotEmpty(id)) {
			return await getById(Notification.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static getAll = async () => {
		return await getAll(Notification.collectionName);
	}

	static getByField = async (fieldName, fieldValue, comparer = '==') => {
		return await getBySingleQuery(Notification.collectionName, fieldName, fieldValue, comparer);
	}

	static getByUserId = async (userId, isStaff = false) => {
		const notifications = await getBySingleQuery(Notification.collectionName, 'userId', userId, '==');
		if (isBooleanTrue(isStaff)) {
			const toStaffNotifications = await getBySingleQuery(Notification.collectionName, 'toStaff', true, '==');
			const notificationList = [...notifications, ...toStaffNotifications];

			return orderByList(notificationList, ['createdAt'], ['asc']);
		} else {
			return orderByList(notifications, ['createdAt'], ['asc']);
		}
	}

	static getByIds = async (ids = []) => {
		return await getByIdQuery(Notification.collectionName, ids, 'in');
	}

	static getCount = async (queryParameters) => {
		const { userId, toStaff, read, start, end } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(userId)) {
			queryConstraints.push(where('userId', 'in', userId));
		} else if (isStringNotEmptyOrZeroLength(userId)) {
			queryConstraints.push(where('userId', '==', userId));
		}

		if (isBooleanTrue(toStaff)) {
			queryConstraints.push(where('toStaff', '==', true));
		} else if (isBooleanFalse(toStaff)) {
			queryConstraints.push(where('toStaff', '==', false));
		}

		if (isBooleanTrue(read)) {
			queryConstraints.push(where('read', '==', true));
		} else if (isBooleanFalse(read)) {
			queryConstraints.push(where('read', '==', false));
		}

		if (isNotNullOrUndefined(start)) {
			let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '>=', startDate));
		}

		if (isNotNullOrUndefined(end)) {
			let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '<=', endDate));
		}

		let q = query(collection(db, Notification.collectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);

		//console.log(snapShot.data());

		return snapShot.data().count;
	}

	static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
		const { userId, toStaff, read, start, end } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(userId)) {
			queryConstraints.push(where('userId', 'in', userId));
		} else if (isStringNotEmptyOrZeroLength(userId)) {
			queryConstraints.push(where('userId', '==', userId));
		}

		if (isBooleanTrue(toStaff)) {
			queryConstraints.push(where('toStaff', '==', true));
		} else if (isBooleanFalse(toStaff)) {
			queryConstraints.push(where('toStaff', '==', false));
		}

		if (isBooleanTrue(read)) {
			queryConstraints.push(where('read', '==', true));
		} else if (isBooleanFalse(read)) {
			queryConstraints.push(where('read', '==', false));
		}

		if (isNotNullOrUndefined(start)) {
			let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '>=', startDate));
		}

		if (isNotNullOrUndefined(end)) {
			let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '<=', endDate));
		}

		let q = null;
		if (isObjectNotEmpty(firstVisible)) {
			q = query(collection(db, Notification.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
		} else if (isObjectNotEmpty(lastVisible)) {
			q = query(collection(db, Notification.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
		} else {
			q = query(collection(db, Notification.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
		}

		let snapShot = await getDocs(q);
		return snapShot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static get = async (queryParameters) => {
		let allRecords = [];
		try {
			let next = null;

			let totalCount = await this.getCount(queryParameters);
			let totalPages = Math.ceil(totalCount / 10000.0);
			// console.log('totalCount: ' + totalCount);
			// console.log('totalPages: ' + totalPages);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				// console.log('starting page ' + page);

				let list = await this.getPaginated(queryParameters, 10000, null, next, 'createdAt');

				// console.log(list.length);
				allRecords = [...allRecords, ...list];

				// console.log(allRecords.length);

				// console.log('go to next page');
				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
		}

		return allRecords;
	}
}

export default Notification;